import {
  type DraftPayment,
  DraftPaymentStatus,
  type InvoiceData,
} from "@/apis/gql/types";
import { Check, CircleOff, SendHorizonal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StatusBadge } from "./status-badge";

type TransferStatus = "transferred" | "no_transfer" | "to_transfer";

export function getTransferStatus(
  invoice: Pick<InvoiceData, "isPaid">,
  draftPayments: Pick<DraftPayment, "status">[],
): TransferStatus {
  const wasTransfered = draftPayments.some(
    (payment) => payment.status !== DraftPaymentStatus.DRAFT,
  );

  if (invoice.isPaid && wasTransfered) {
    return "transferred";
  }

  if (invoice.isPaid) {
    return "no_transfer";
  }

  return "to_transfer";
}

export const BankTransferStatusCell = ({
  invoice,
  draftPayments,
}: {
  invoice: Pick<InvoiceData, "isPaid">;
  draftPayments: Pick<DraftPayment, "status">[];
}) => {
  const { t } = useTranslation();

  const transferStatus = getTransferStatus(invoice, draftPayments);

  if (transferStatus === "transferred") {
    return (
      <StatusBadge
        icon={Check}
        iconColor="text-green-800"
        textColor="text-green-800"
        containerColor="bg-green-100 border border-green-200"
      >
        {t("transfer_states.transferred")}
      </StatusBadge>
    );
  }

  if (transferStatus === "no_transfer") {
    return (
      <StatusBadge
        icon={CircleOff}
        iconColor="text-gray-800"
        textColor="text-gray-800"
        containerColor="bg-gray-100 border border-gray-200"
      >
        {t("transfer_states.no_transfer")}
      </StatusBadge>
    );
  }

  return (
    <StatusBadge
      icon={SendHorizonal}
      iconColor="text-orange-600"
      textColor="text-orange-800"
      containerColor="bg-orange-100 border border-orange-200"
    >
      {t("transfer_states.to_transfer")}
    </StatusBadge>
  );
};
